import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Bot, ChevronDown, ChevronRight, FileDiff, Loader2, Plus, Send, Square, Table2, Wrench, X } from "lucide-react";
import { Alert, Button, Textarea } from "@/components/ui";
import { useContextApi, isAbortError, type ChatEvent, type Proposal } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { formatBytes, formatUsd, Markdown, newSessionId, StatusBadge, useErrorHandler } from "./shared";

const SESSION_KEY = "databasecontext.chat.session_id";

type ChatItem =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string }
  | { kind: "tool"; name: string; args?: unknown; summary?: string; bytes_billed?: number }
  | { kind: "proposal"; id: string; proposal?: Proposal }
  | { kind: "done"; cost_usd?: number; bytes_billed?: number }
  | { kind: "error"; text: string }
  | { kind: "note"; text: string };

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = newSessionId();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return newSessionId();
  }
}

const TOOL_LABEL: Record<string, string> = {
  search_cards: "Kaarten doorzocht",
  read_card: "Kaart gelezen",
  list_failed_results: "Mislukte evaluatievragen opgehaald",
  run_sql: "Query uitgevoerd",
  profile_column: "Kolom geprofileerd",
  propose_change: "Voorstel gemaakt",
};

function ToolRow({ item }: { item: Extract<ChatItem, { kind: "tool" }> }) {
  const [open, setOpen] = useState(false);
  const hasArgs = item.args !== undefined && item.args !== null && !(typeof item.args === "object" && Object.keys(item.args as object).length === 0);
  return (
    <div className="rounded-md border border-border bg-sl-grey-50 text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
        aria-expanded={open}
        disabled={!hasArgs}
      >
        {hasArgs ? (
          open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <Wrench className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="shrink-0 font-medium" title={item.name}>
          {TOOL_LABEL[item.name] ?? item.name.replace(/_/g, " ")}
        </span>
        {item.summary && <span className="min-w-0 flex-1 truncate text-muted-foreground" title={item.summary}>{item.summary}</span>}
        {item.bytes_billed !== undefined && item.bytes_billed !== null && (
          <span className="ml-auto shrink-0 tabular-nums text-muted-foreground" title="Gefactureerde bytes">
            {formatBytes(item.bytes_billed)}
          </span>
        )}
      </button>
      {open && hasArgs && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all border-t border-border px-2.5 py-2 font-mono text-[11px] text-foreground">
          {typeof item.args === "string" ? item.args : JSON.stringify(item.args, null, 2)}
        </pre>
      )}
    </div>
  );
}

function suggestions(table?: string): string[] {
  if (!table) {
    return [
      "Wat betekent omzet in fact_transportorderomzet en klopt de huidige kaart?",
      "Welke tabellen gebruik ik voor het aantal actieve chauffeurs per maand?",
      "Welke valkuilen ontbreken nog bij dim_klanten?",
    ];
  }
  const t = table.split(".").pop();
  return [`Leg de granulariteit van ${t} uit en controleer die met een query.`, `Kloppen de joins van ${t}? Controleer ze tegen de data.`, `Stel een verbetering voor van de kaart van ${t}.`];
}

export function OrchestratorChat({
  onProposal,
  onOpenProposal,
  onClose,
  contextTable,
  onClearContext,
  focusNonce,
}: {
  onProposal: (p: Proposal) => void;
  onOpenProposal: (id: string) => void;
  onClose: () => void;
  /** Table currently open in the catalog: shown as context and used in suggestions. */
  contextTable?: string;
  onClearContext?: () => void;
  /** Bump to focus the input (e.g. after "Vraag de assistent"). */
  focusNonce?: number;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [sessionId, setSessionId] = useState(getSessionId);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusNonce) inputRef.current?.focus();
  }, [focusNonce]);

  // Load history for this session (ignore failures: a new session has none).
  useEffect(() => {
    const ac = new AbortController();
    api
      .getChatHistory(sessionId, ac.signal)
      .then((h) => {
        if (!h) return;
        const restored: ChatItem[] = (h.messages ?? []).map((m) => (m.role === "user" ? { kind: "user", text: m.text } : { kind: "assistant", text: m.text }));
        (h.proposals ?? []).forEach((id) => restored.push({ kind: "proposal", id: String(id) }));
        setItems((cur) => (cur.length ? cur : restored));
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, [api, sessionId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items, status]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const push = (it: ChatItem) => setItems((cur) => [...cur, it]);

  const onEvent = useCallback(
    (e: ChatEvent) => {
      switch (e.type) {
        case "status":
          setStatus(e.text);
          break;
        case "tool":
          push({ kind: "tool", name: e.name, args: e.args, summary: e.summary, bytes_billed: e.bytes_billed });
          break;
        case "message":
          // Each message event is a markdown block; consecutive blocks join into one assistant bubble.
          setItems((cur) => {
            const last = cur[cur.length - 1];
            if (last?.kind === "assistant") return [...cur.slice(0, -1), { kind: "assistant", text: `${last.text}\n\n${e.text}` }];
            return [...cur, { kind: "assistant", text: e.text }];
          });
          break;
        case "proposal":
          if (e.proposal?.id) {
            push({ kind: "proposal", id: e.proposal.id, proposal: e.proposal });
            onProposal(e.proposal);
          }
          break;
        case "done":
          setStatus(null);
          push({ kind: "done", cost_usd: e.cost_usd, bytes_billed: e.bytes_billed });
          break;
        case "error":
          setStatus(null);
          push({ kind: "error", text: e.text || "Onbekende fout" });
          break;
      }
    },
    [onProposal],
  );

  const send = async () => {
    const typed = input.trim();
    if (!typed || streaming) return;
    const t = contextTable?.split(".").pop();
    // Give the assistant the open table as context unless the question already names it.
    const text = contextTable && t && !typed.includes(t) ? `Over tabel ${contextTable}: ${typed}` : typed;
    setInput("");
    push({ kind: "user", text: typed });
    setStreaming(true);
    setStatus("Bezig…");
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await api.chat({ session_id: sessionId, message: text }, onEvent, ac.signal);
    } catch (e) {
      if (isAbortError(e)) push({ kind: "note", text: "Gestopt." });
      else {
        const msg = handleError(e);
        if (msg) push({ kind: "error", text: msg });
      }
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
      setStreaming(false);
      setStatus(null);
    }
  };

  const stop = () => abortRef.current?.abort();

  const newConversation = () => {
    abortRef.current?.abort();
    const id = newSessionId();
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
      /* storage unavailable */
    }
    setItems([]);
    setSessionId(id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Bot className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h2 className="truncate text-base font-semibold">Contextassistent</h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button variant="ghost" size="sm" onClick={newConversation} title="Nieuw gesprek">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nieuw gesprek</span>
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Assistent sluiten" title="Sluiten (Esc)">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {contextTable && (
        <div className="flex items-center gap-2 border-b border-border bg-sl-grey-50 px-4 py-2 text-xs">
          <span className="text-muted-foreground">Context:</span>
          <span className="inline-flex min-w-0 items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 font-mono">
            <Table2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{contextTable}</span>
          </span>
          {onClearContext && (
            <button type="button" onClick={onClearContext} className="ml-auto shrink-0 font-medium text-muted-foreground hover:text-foreground">
              Zonder context
            </button>
          )}
        </div>
      )}
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4" aria-live="polite">
        {items.length === 0 && !streaming && (
          <div className="flex h-full flex-col justify-center gap-3 text-sm">
            <p className="text-muted-foreground">
              De assistent onderzoekt de data met echte queries en stelt onderbouwde wijzigingen voor. Niets gaat live zonder jouw klik bij Voorstellen.
            </p>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Probeer</p>
            <div className="space-y-2">
              {suggestions(contextTable).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    setInput(q);
                    inputRef.current?.focus();
                  }}
                  className="block w-full rounded-md border border-border px-3 py-2 text-left text-sm transition-colors hover:border-sl-grey-300 hover:bg-sl-grey-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {items.map((it, i) => {
          switch (it.kind) {
            case "user":
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground [overflow-wrap:anywhere]">{it.text}</div>
                </div>
              );
            case "assistant":
              return (
                <div key={i} className="max-w-[95%] rounded-lg border border-border bg-card px-3 py-2">
                  <Markdown>{it.text}</Markdown>
                </div>
              );
            case "tool":
              return <ToolRow key={i} item={it} />;
            case "proposal":
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onOpenProposal(it.id)}
                  className="flex w-full items-center gap-3 rounded-md border border-sl-yellow/60 bg-sl-yellow-soft px-3 py-2.5 text-left text-sm transition-colors hover:border-sl-charcoal"
                >
                  <FileDiff className="h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-muted-foreground">Nieuw voorstel</div>
                    <div className="truncate font-medium">{it.proposal?.title ?? it.id}</div>
                  </div>
                  {it.proposal?.status && <StatusBadge status={it.proposal.status} />}
                  <span className="shrink-0 text-xs font-medium underline underline-offset-2">Bekijken</span>
                </button>
              );
            case "done":
              return (
                <details key={i} className="text-right text-xs text-muted-foreground">
                  <summary className="cursor-pointer select-none list-none hover:text-foreground">Klaar · details</summary>
                  <span className="tabular-nums">
                    kosten {formatUsd(it.cost_usd)} · {formatBytes(it.bytes_billed)} gescand · sessie {sessionId.slice(0, 8)}
                  </span>
                </details>
              );
            case "error":
              return (
                <Alert key={i} tone="danger">
                  {it.text}
                </Alert>
              );
            case "note":
              return (
                <div key={i} className="text-center text-xs text-muted-foreground">
                  {it.text}
                </div>
              );
          }
        })}
        {streaming && status && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="truncate">{status}</span>
          </div>
        )}
      </div>
      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            placeholder={contextTable ? `Vraag iets over ${contextTable.split(".").pop()}…` : "Stel een vraag of vraag om een verbetering…"}
            className="min-h-[2.75rem] resize-none"
            aria-label="Bericht"
            disabled={streaming}
          />
          {streaming ? (
            <Button variant="outline" onClick={stop} aria-label="Stoppen" title="Stoppen">
              <Square className="h-4 w-4" />
              Stop
            </Button>
          ) : (
            <Button onClick={() => void send()} disabled={!input.trim()} aria-label="Versturen" title="Versturen (Enter)">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className={cn("mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground")}>
          <span>Enter verstuurt · Shift+Enter nieuwe regel</span>

        </div>
      </div>
    </div>
  );
}
